import { useAuth } from "@clerk/clerk-react";
import { Box, Divider, Grid, Typography } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import lodash from "lodash";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { OrganizationAutopilotProfileStatus } from "schemas/dashboard";
import { routes } from "schemas/routes";

import StyledLargeButton from "components/Styled/Button/StyledLargeButton";
import { useAccountReviewState } from "features/Auth/Onboarding/shared/AccountReviewUpsell";
import { fetcherAuth } from "utils/api";
import { isPage } from "utils/general";

import {
  AutopilotOnboardingStep,
  DefaultAutopilotOnboardingStepInfo,
  DefaultAutopilotOnboardingSteps,
} from "../schema";
import styles from "./styles";

const AutopilotOnboardingProgress = () => {
  const { getToken } = useAuth();
  const { setErrorAlert } = useContext(AlertContext);
  const [resumeLoading, setResumeLoading] = useState(false);
  const navigate = useNavigate();

  const { organizationAutopilotProfile, currentOrg } = useContext(
    OrganizationUserContext,
  );
  const { hasPurchasedAccountReview } = useAccountReviewState();

  const isComplete = (index: number) => {
    if (isInMiddleOfDifferentOnboarding()) return false;
    return (
      index <
      (organizationAutopilotProfile?.onboardingState?.currentStepIndex || 0)
    );
  };

  const calculatePercentageOfCompletion = () => {
    const currentStepIndex =
      organizationAutopilotProfile?.onboardingState?.currentStepIndex || 0;
    const totalSteps = DefaultAutopilotOnboardingSteps.length;
    return Math.round((currentStepIndex / totalSteps) * 100);
  };

  const isInMiddleOfDifferentOnboarding = () => {
    if (!organizationAutopilotProfile?.onboardingState) return false;
    return !lodash.isEqual(
      organizationAutopilotProfile.onboardingState.steps,
      DefaultAutopilotOnboardingSteps,
    );
  };

  const handleProceed = async () => {
    setResumeLoading(true);
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/onboarding/autopilot-status`,
        "PATCH",
        {},
        {
          status: OrganizationAutopilotProfileStatus.pending,
        },
      );
      navigate(`/${routes.onboardingAutopilot}`);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setResumeLoading(false);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: isPage(routes.autopilot) ? "#F2F3FF" : "white",
        py: 3,
        px: isPage(routes.autopilot) ? { xs: 2, md: 4 } : 0,
        borderRadius: 4,
      }}
    >
      {!isPage(routes.autopilot) && (
        <Divider sx={{ my: 3, backgroundColor: "purple.light" }} />
      )}

      <Grid container gap={2} alignItems={"center"}>
        <Grid item xs md>
          {isInMiddleOfDifferentOnboarding() ? (
            <Typography
              gutterBottom
              sx={{ fontSize: { xs: 14, md: 18, fontWeight: 600 } }}
            >
              We have updated our onboarding process since your last login
            </Typography>
          ) : (
            <Typography
              sx={{
                textTransform: "uppercase",
                fontWeight: 600,
                fontSize: { xs: 14, md: 18 },
              }}
              gutterBottom
            >
              You're {calculatePercentageOfCompletion()}% set up!
            </Typography>
          )}

          <Typography sx={{ fontSize: 16 }}>
            {isInMiddleOfDifferentOnboarding()
              ? "But don't worry, we still store your old information. Click Proceed Onboarding Setup to resume."
              : "Autopilot users who complete onboarding get better results from automated outreach."}
          </Typography>
        </Grid>

        <Grid item xs={12} md="auto">
          <StyledLargeButton onClick={handleProceed} loading={resumeLoading}>
            Complete Onboarding Setup
          </StyledLargeButton>
        </Grid>
      </Grid>

      <Grid item xs={12} container gap={1} sx={{ mt: 3 }}>
        {DefaultAutopilotOnboardingSteps?.map(
          (step: AutopilotOnboardingStep, index) =>
            step in DefaultAutopilotOnboardingStepInfo ? (
              <Box
                key={step}
                sx={
                  isComplete(index)
                    ? styles.completeChip
                    : styles.incompleteChip
                }
              >
                {isComplete(index) ? (
                  <Box sx={styles.circle}>
                    <Box
                      component="i"
                      className="fa-regular fa-check"
                      sx={{ color: "white" }}
                    />
                  </Box>
                ) : (
                  <></>
                )}
                {DefaultAutopilotOnboardingStepInfo[step]?.name}

                {isComplete(index) ? (
                  ""
                ) : (
                  <Typography sx={styles.timeText}>
                    {
                      DefaultAutopilotOnboardingStepInfo[
                        step as AutopilotOnboardingStep
                      ]?.time
                    }{" "}
                    min
                  </Typography>
                )}
              </Box>
            ) : null,
        )}
        {hasPurchasedAccountReview && (
          <Box sx={styles.incompleteChip}>
            Setup Account
            <Typography sx={styles.timeText}>1 min</Typography>
          </Box>
        )}
      </Grid>
    </Box>
  );
};

export default AutopilotOnboardingProgress;
